import { Box, Button, Group, Modal, NumberInput, Select, SimpleGrid, Stack, Text, Textarea } from "@mantine/core";
import { LockIcon, WarningIcon } from "@phosphor-icons/react";
import { useDisclosure } from "@mantine/hooks";
import { ConfirmNote, DisabledReason, HelpButtonGroup, i18n, nextStepToast, showNotification } from "@dehub/theme";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReturnApi } from "../../apis";
import { currencyFormat } from "../../utils/currency";
import { dateFormat } from "../../utils/date";
import { ReasonTooltip } from "../ui";
import { reasonLabel } from "./atoms";
import { ReplacementFeasibilityWidget } from "./replacement-feasibility-widget";
import { DisputeOverrideModal } from "./dispute-override-modal";
import { RefundConfirmModal } from "./refund-confirm-modal";
import { ReturnAction, ReturnActionState, returnHelpId, useReturnActions } from "./return-actions";
import type { IReturnDetail, RefundMethod, ResolutionType, ShippingResponsibility } from "../../apis/return";

interface Props {
  detail: IReturnDetail;
  onChange: () => void;
  /** Open one dialog on mount (list row «→» navigates here with `state.openAction`). */
  initialAction?: string | null;
  /** Tenant dispute ceiling (list meta.disputeMaxRounds). Default 3. */
  disputeMax?: number;
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

/** Confirmation / small form dialog width (DeHUB UI standard, canvas Ord_Rules «д»). */
const MODAL_W = 440;
/** BE set-resolution.ts / escalate.ts / escalate-dispute.ts: reason Joi.string().min(3). */
const MIN_REASON = 3;

const RESOLUTION_VALUES: ResolutionType[] = ["REFUND_MONEY", "REPLACEMENT", "NEXT_ORDER_DISCOUNT", "NONE", "MIXED"];
const REFUND_METHOD_VALUES: RefundMethod[] = ["BANK_TRANSFER", "INVOICE_DEDUCTION", "CASH", "CREDIT_BALANCE"];
const SHIPPING_VALUES: ShippingResponsibility[] = ["BUYER_DELIVERS", "SELLER_PICKS_UP", "BUYER_SHIPS", "SELLER_PAYS_SHIPPING", "NOT_REQUIRED"];

const resolutionOptions = (t: TFn) => RESOLUTION_VALUES.map((v) => ({ value: v, label: t(`resolutionType.${v}`) }));
const shippingOptions = (t: TFn) => SHIPPING_VALUES.map((v) => ({ value: v, label: t(`shipping.responsibility.${v}`) }));
// Static refund-ETA copy by channel (no backend call).
const refundEta = (m: RefundMethod) => i18n.t(`returns:actionBar.eta.${m}`);

/** Success toast; with a @dehub/help key the toast says what happens next (R8), the message stays the fallback. */
function notifyOk (msg: string, helpKey?: string){
  if (helpKey) {
    nextStepToast(helpKey, { fallback: { title: i18n.t("returns:toast.success"), message: msg } });
    return;
  }
  showNotification({ color: "green", title: i18n.t("returns:toast.success"), message: msg });
}

/** @dehub/help key namespace of the return detail (spec 12 §0). */
const HELP = "order.returns-detail";
function notifyErr (err: unknown){
  showNotification({ color: "red", title: i18n.t("returns:toast.error"), message: (err as Error).message });
}

/** Dialog title: action name + «RET-… · шалтгаан» (canvas Ord_ReturnActions). */
function DialogTitle ({ title, detail }: { title: string; detail: IReturnDetail }){
  return (
    <Stack gap={2}>
      <Text fz="var(--nw-fz-drawer)" fw={650}>{title}</Text>
      <Text size="sm" c="var(--nw-text-2)">
        {detail.returnNumber} · {reasonLabel(detail.returnReason) || "-"}
      </Text>
    </Stack>
  );
}

/** Permission codes are internal role keys and are not shown to users (2026-10-29). */
function PermissionLine (_props: { code: string }){
  return null;
}

/** Footer: «Болих» + submit; an invalid form keeps the submit visible with the reason. */
function DialogFooter ({ onCancel, submitLabel, onSubmit, busy, invalidReason }: {
  onCancel: () => void; submitLabel: string; onSubmit: () => void; busy: boolean; invalidReason?: string | null;
}){
  const { t } = useTranslation("returns");
  return (
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={onCancel}>{t("actionsDialog.cancel")}</Button>
      <ReasonTooltip reason={invalidReason || undefined}>
        <Button onClick={onSubmit} loading={busy}>{submitLabel}</Button>
      </ReasonTooltip>
    </Group>
  );
}

/**
 * One action of the bar: button + attached «?» (R2) + one caption line (why it is unavailable).
 * While unavailable the whole button opens the reason popover (R4, `order.returns-detail.reason.{id}`).
 */
function BarAction ({ label, state, primary, onClick, busy, helpId }: {
  label: string; state: ReturnActionState; primary?: boolean; onClick: () => void; busy: boolean; helpId: string;
}){
  // Enabled actions carry no caption (the required permission code is not shown to users).
  const caption: ReactNode = state.enabled ? null : state.reason;
  return (
    <Stack gap={6} align="flex-start">
      <DisabledReason helpKey={`${HELP}.reason.${helpId}`} reason={state.code} when={!state.enabled} position="bottom-start">
        <HelpButtonGroup helpKey={`${HELP}.action.${helpId}`}>
          <Button
            data-help-id={`return-${helpId}`}
            variant={state.enabled ? (primary ? "filled" : "default") : "light"}
            color={state.enabled ? (primary ? "dark" : undefined) : "gray"}
            leftSection={state.enabled ? undefined : <LockIcon size={16} />}
            onClick={onClick}
            loading={busy && state.enabled}
          >
            {label}
          </Button>
        </HelpButtonGroup>
      </DisabledReason>
      {caption ? <Text size="sm" c="var(--nw-text-2)" translate="no">{caption}</Text> : null}
    </Stack>
  );
}

/**
 * Detail action bar (canvas Ord_ReturnDetail + Ord_ReturnActions). Always shows the five core
 * steps; the other steps appear when the status and the user's side allow them. Nothing is hidden
 * for a missing permission: the button is aria-disabled and the reason sits under it.
 */
export function ReturnActionBar ({ detail, onChange, initialAction, disputeMax = 3 }: Props){
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { stateOf, required } = useReturnActions({ disputeMax });

  const [proposeOpen, proposeHandlers] = useDisclosure(false);
  const [rejectOpen, rejectHandlers] = useDisclosure(false);
  const [acceptOpen, acceptHandlers] = useDisclosure(false);
  const [disputeOpen, disputeHandlers] = useDisclosure(false);
  const [cancelOpen, cancelHandlers] = useDisclosure(false);
  const [confirmOpen, confirmHandlers] = useDisclosure(false);
  const [setResOpen, setResHandlers] = useDisclosure(false);
  const [escOpen, escHandlers] = useDisclosure(false);
  const [overrideOpen, overrideHandlers] = useDisclosure(false);
  const [busy, setBusy] = useState(false);
  /** Field errors appear after the first submit attempt (or once the user typed). */
  const [submitted, setSubmitted] = useState(false);

  // ── Local form states ──
  const hasInvoice = !!detail.invoiceId;
  const defaultMethod: RefundMethod =
    detail.preferredRefundMethod && (detail.preferredRefundMethod !== "INVOICE_DEDUCTION" || hasInvoice)
      ? detail.preferredRefundMethod
      : hasInvoice ? "INVOICE_DEDUCTION" : "BANK_TRANSFER";
  const [refundPercent, setRefundPercent] = useState<number>(100);
  const [deductionReason, setDeductionReason] = useState("");
  const [shipping, setShipping] = useState<ShippingResponsibility>(detail.shippingResponsibility ?? "BUYER_DELIVERS");
  const [resolution, setResolution] = useState<ResolutionType>(detail.resolutionType ?? "REFUND_MONEY");
  const [refundMethod, setRefundMethod] = useState<RefundMethod | null>(defaultMethod);
  const [proposalNotes, setProposalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [buyerNotes, setBuyerNotes] = useState("");
  const [disputeNotes, setDisputeNotes] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [escReason, setEscReason] = useState("");

  const refundMethodOptions = REFUND_METHOD_VALUES.map((v) => ({
    value   : v,
    label   : v === "INVOICE_DEDUCTION" && !hasInvoice ? `${t(`refundMethod.${v}`)} ${t("actionBar.invoiceUnavailable")}` : t(`refundMethod.${v}`),
    disabled: v === "INVOICE_DEDUCTION" && !hasInvoice
  }));

  const open = (fn: () => void) => () => {
    setSubmitted(false);
    fn();
  };

  // ── Validation (O05, O06) ──
  const deductionMissing = refundPercent < 100 && !deductionReason.trim();
  const methodChanged = resolution === "REFUND_MONEY" && !!detail.preferredRefundMethod && refundMethod !== detail.preferredRefundMethod;
  const methodNoteMissing = methodChanged && !proposalNotes.trim();
  const percentInvalid = !(refundPercent >= 0 && refundPercent <= 100);
  const proposeInvalid = deductionMissing || methodNoteMissing || percentInvalid || (resolution === "REFUND_MONEY" && !refundMethod);
  const rejectInvalid = !rejectionReason.trim();
  const disputeInvalid = !disputeNotes.trim();
  const overrideLen = overrideReason.trim().length;
  const setResInvalid = overrideLen < MIN_REASON || (resolution === "REFUND_MONEY" && !refundMethod);
  const escInvalid = escReason.trim().length < MIN_REASON;

  const wrap = (invalid: boolean, fn: () => Promise<unknown>) => async () => {
    setSubmitted(true);
    if (invalid) return;
    try {
      setBusy(true);
      await fn();
      onChange();
    } catch (e) {
      notifyErr(e);
    } finally {
      setBusy(false);
    }
  };

  const handlePropose = wrap(proposeInvalid, async () => {
    await ReturnApi.propose(detail.id, {
      refundPercent,
      // O05 — the reason is sent only below 100% and never as a placeholder.
      deductionReason: refundPercent < 100 ? deductionReason.trim() : null,
      shippingResponsibility: shipping,
      proposalNotes  : proposalNotes.trim() || null,
      resolutionType : resolution,
      refundMethod   : resolution === "REFUND_MONEY" ? refundMethod : null,
      expectedVersion: detail.version
    });
    proposeHandlers.close();
    notifyOk(t("resolutionPanel.toast.proposalSent"), `${HELP}.toast.propose`);
  });

  const handleReject = wrap(rejectInvalid, async () => {
    await ReturnApi.reject(detail.id, { rejectionReason: rejectionReason.trim(), expectedVersion: detail.version });
    rejectHandlers.close();
    notifyOk(t("actionBar.toast.rejected"), `${HELP}.toast.reject`);
  });

  const handleAccept = wrap(false, async () => {
    await ReturnApi.accept(detail.id, { buyerNotes: buyerNotes.trim() || null, expectedVersion: detail.version });
    acceptHandlers.close();
    notifyOk(t("actionBar.toast.accepted"));
  });

  const handleDispute = wrap(disputeInvalid, async () => {
    await ReturnApi.dispute(detail.id, { disputeNotes: disputeNotes.trim(), expectedVersion: detail.version });
    disputeHandlers.close();
    notifyOk(t("actionBar.toast.disputed"));
  });

  const handleCancel = wrap(false, async () => {
    await ReturnApi.cancel(detail.id, { reason: cancelReason.trim() || null, expectedVersion: detail.version });
    cancelHandlers.close();
    notifyOk(t("actionBar.toast.cancelled"));
  });

  const handleSetResolution = wrap(setResInvalid, async () => {
    await ReturnApi.setResolution(detail.id, {
      resolutionType : resolution,
      refundMethod   : resolution === "REFUND_MONEY" ? refundMethod : null,
      reason         : overrideReason.trim(),
      managerOverride: ["ACCEPTED", "AWAITING_RESOLUTION"].includes(detail.status),
      expectedVersion: detail.version
    });
    setResHandlers.close();
    notifyOk(t("actionBar.toast.resolutionUpdated"), `${HELP}.toast.set-resolution`);
  });

  const handleEscalate = wrap(escInvalid, async () => {
    await ReturnApi.escalate(detail.id, { reason: escReason.trim(), expectedVersion: detail.version });
    escHandlers.close();
    setEscReason("");
    notifyOk(t("actionBar.toast.escalated"), `${HELP}.toast.escalate`);
  });

  // ── Matrix ──
  const OPENERS: Partial<Record<ReturnAction, () => void>> = {
    propose        : open(proposeHandlers.open),
    reject         : open(rejectHandlers.open),
    accept         : open(acceptHandlers.open),
    dispute        : open(disputeHandlers.open),
    cancel         : open(cancelHandlers.open),
    setResolution  : open(setResHandlers.open),
    escalate       : open(escHandlers.open),
    confirmRefund  : confirmHandlers.open,
    managerOverride: overrideHandlers.open,
    offSystemVerify: () => navigate(`/returns/${detail.id}/off-system-verify`),
    warehouseReceive: () => navigate(`/returns/${detail.id}/warehouse-receipt`)
  };

  // Core steps are always shown; the rest only when status and side fit.
  const CORE: ReturnAction[] = ["propose", "reject", "setResolution", "cancel", "escalate"];
  const EXTRA: ReturnAction[] = ["offSystemVerify", "warehouseReceive", "confirmRefund", "accept", "dispute", "managerOverride"];
  const items = [
    ...EXTRA.map((a) => ({ action: a, state: stateOf(a, detail) })).filter((x) => x.state.applies),
    ...CORE.map((a) => ({ action: a, state: stateOf(a, detail) }))
  ];
  const primary = items.find((x) => x.state.enabled)?.action;

  // List row «→» opens its dialog here once.
  useEffect(() => {
    if (!initialAction) return;
    const action = initialAction as ReturnAction;
    if (stateOf(action, detail).enabled) OPENERS[action]?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction]);

  // Read-only refund preview: refundPercent applied to the return's refundable base.
  const refundBase = detail.totalRequestedAmount;
  const refundAmount = typeof refundBase === "number" && !isNaN(refundBase) ? Math.round((refundBase * refundPercent) / 100) : null;

  const verifyStatus = detail.sellerVerificationStatus;
  const offSystemDecided = detail.isOffSystemBuyerClaim && !!verifyStatus && verifyStatus !== "PENDING";

  return (
    <>
      {detail.disputeCount >= disputeMax ? (
        <Group gap="sm" mb="md" wrap="nowrap" align="flex-start">
          <WarningIcon size={18} aria-hidden />
          <Text size="sm">
            <Text span fw={600}>{t("actionBar.escalatedTitle")}.</Text> {t("actionBar.escalatedBody", { count: detail.disputeCount })}
          </Text>
        </Group>
      ) : null}

      {offSystemDecided ? (
        <Text size="sm" c="var(--nw-text-2)" mb="md">
          {t("actionBar.offSystemVerification")}:{" "}
          <Text span fw={600} c="var(--nw-text)">{t(`verify.${verifyStatus}`, { defaultValue: "-" })}</Text>
          {detail.sellerVerifiedAt ? ` · ${dateFormat(detail.sellerVerifiedAt)}` : ""}
          {detail.sellerVerificationNotes ? ` · ${detail.sellerVerificationNotes}` : ""}
        </Text>
      ) : null}

      {/* One row of up to five actions like Ord_ReturnDetail; a long permission caption wraps inside its column. */}
      <SimpleGrid cols={{ base: 2, md: Math.min(Math.max(items.length, 1), 5) }} spacing="md" role="group" aria-label={t("actions.groupLabel")}>
        {items.map(({ action, state }) => (
          <BarAction
            key={action}
            label={action === "dispute"
              ? t("actionBar.dispute", { count: detail.disputeCount, max: disputeMax })
              : t(`actions.label.${action}`)}
            state={state}
            helpId={returnHelpId(action)}
            primary={action === primary}
            busy={busy}
            onClick={() => OPENERS[action]?.()}
          />
        ))}
      </SimpleGrid>

      {/* PROPOSE */}
      <Modal opened={proposeOpen} onClose={proposeHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.propose")} detail={detail} />}>
        <Stack gap="md">
          <NumberInput
            label={t("actionsDialog.propose.percent")}
            description={t("actionsDialog.propose.percentHint")}
            value={refundPercent}
            min={0}
            max={100}
            suffix=" %"
            required
            error={submitted && percentInvalid ? t("actionsDialog.propose.percentHint") : undefined}
            onChange={(v) => setRefundPercent(typeof v === "number" ? v : Number(v) || 0)}
          />
          <Text size="sm" c="var(--nw-text-2)">
            {t("resolutionPanel.refundAmountLabel")}:{" "}
            <Text span fw={600} c="var(--nw-text)" ff="monospace">{refundAmount != null ? currencyFormat(refundAmount) : "-"}</Text>
          </Text>
          {refundPercent < 100 ? (
            <Textarea
              label={t("actionsDialog.propose.deductionReason")}
              placeholder={t("actionsDialog.propose.deductionPlaceholder")}
              value={deductionReason}
              onChange={(e) => setDeductionReason(e.currentTarget.value)}
              required
              autosize
              minRows={2}
              error={submitted && deductionMissing ? t("actionsDialog.propose.deductionRequired") : undefined}
              description={submitted && deductionMissing ? undefined : t("actionsDialog.propose.deductionRequired")}
            />
          ) : null}
          <Select label={t("actionsDialog.propose.shipping")} data={shippingOptions(t)} value={shipping}
            onChange={(v) => v && setShipping(v as ShippingResponsibility)} required allowDeselect={false} />
          <Select label={t("actionsDialog.resolution")} data={resolutionOptions(t)} value={resolution}
            onChange={(v) => v && setResolution(v as ResolutionType)} required allowDeselect={false} />
          <ReplacementFeasibilityWidget
            returnRequestId={detail.id}
            enabled={proposeOpen && (resolution === "REPLACEMENT" || resolution === "MIXED")}
          />
          {resolution === "REFUND_MONEY" ? (
            <Select
              label={t("actionsDialog.propose.method")}
              description={
                (detail.preferredRefundMethod ? t("actionsDialog.propose.methodHint") : "") +
                (refundMethod ? ` ${t("actionBar.etaLabel")}: ${refundEta(refundMethod)}.` : "")
              }
              data={refundMethodOptions}
              value={refundMethod ?? null}
              onChange={(v) => setRefundMethod(v as RefundMethod)}
              required
              allowDeselect={false}
            />
          ) : null}
          <Textarea
            label={methodChanged ? t("actionsDialog.propose.notesRequired") : t("actionBar.notesOptional")}
            value={proposalNotes}
            onChange={(e) => setProposalNotes(e.currentTarget.value)}
            required={methodChanged}
            autosize
            minRows={2}
            error={submitted && methodNoteMissing ? t("actionsDialog.propose.methodNoteMissing") : undefined}
          />
          <PermissionLine code={required("propose")} />
          <ConfirmNote helpKey={`${HELP}.confirm.propose`} mt={0} />
          <DialogFooter
            onCancel={proposeHandlers.close}
            submitLabel={t("actionsDialog.propose.submit")}
            onSubmit={handlePropose}
            busy={busy}
            invalidReason={proposeInvalid && submitted ? t("actionsDialog.fixFields") : null}
          />
        </Stack>
      </Modal>

      {/* REJECT */}
      <Modal opened={rejectOpen} onClose={rejectHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.reject")} detail={detail} />}>
        <Stack gap="md">
          <Textarea
            label={t("actionsDialog.reject.reason")}
            placeholder={t("actionsDialog.reasonPlaceholder")}
            required
            autosize
            minRows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
            error={submitted && rejectInvalid ? t("actionsDialog.reasonRequired") : undefined}
            description={submitted && rejectInvalid ? undefined : t("actionsDialog.reasonRequired")}
          />
          <Text size="sm" c="var(--nw-text-2)">{t("actionsDialog.reject.visibleToBuyer")}</Text>
          <PermissionLine code={required("reject")} />
          <ConfirmNote helpKey={`${HELP}.confirm.reject`} mt={0} />
          <DialogFooter onCancel={rejectHandlers.close} submitLabel={t("actions.label.reject")} onSubmit={handleReject} busy={busy}
            invalidReason={rejectInvalid && submitted ? t("actionsDialog.reasonRequired") : null} />
        </Stack>
      </Modal>

      {/* ACCEPT (buyer) */}
      <Modal opened={acceptOpen} onClose={acceptHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.accept")} detail={detail} />}>
        <Stack gap="md">
          <Box>
            <Text size="sm" c="var(--nw-text-2)">{t("actionsDialog.accept.terms")}</Text>
            <SimpleGrid cols={2} spacing="xs" mt="xs">
              <Text size="sm" c="var(--nw-text-2)">{t("detail.field.refundPercent")}</Text>
              <Text size="sm" fw={600}>{detail.refundPercent != null ? `${detail.refundPercent}%` : "-"}</Text>
              <Text size="sm" c="var(--nw-text-2)">{t("resolutionPanel.refundAmountLabel")}</Text>
              <Text size="sm" fw={600} ff="monospace">{detail.refundAmount != null ? currencyFormat(detail.refundAmount) : "-"}</Text>
              <Text size="sm" c="var(--nw-text-2)">{t("actionsDialog.resolution")}</Text>
              <Text size="sm" fw={600}>{detail.resolutionType ? t(`resolutionType.${detail.resolutionType}`) : "-"}</Text>
            </SimpleGrid>
          </Box>
          <Textarea label={t("bulkAction.noteOptional")} value={buyerNotes} autosize minRows={2}
            onChange={(e) => setBuyerNotes(e.currentTarget.value)} />
          <PermissionLine code={required("accept")} />
          <DialogFooter onCancel={acceptHandlers.close} submitLabel={t("actions.label.accept")} onSubmit={handleAccept} busy={busy} />
        </Stack>
      </Modal>

      {/* DISPUTE (buyer) */}
      <Modal opened={disputeOpen} onClose={disputeHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actionBar.disputeTitle")} detail={detail} />}>
        <Stack gap="md">
          <Textarea label={t("actionBar.disputeNotes")} required autosize minRows={3} value={disputeNotes}
            onChange={(e) => setDisputeNotes(e.currentTarget.value)}
            error={submitted && disputeInvalid ? t("actionsDialog.reasonRequired") : undefined} />
          <PermissionLine code={required("dispute")} />
          <DialogFooter onCancel={disputeHandlers.close} submitLabel={t("bulkAction.send")} onSubmit={handleDispute} busy={busy}
            invalidReason={disputeInvalid && submitted ? t("actionsDialog.reasonRequired") : null} />
        </Stack>
      </Modal>

      {/* CANCEL (buyer) */}
      <Modal opened={cancelOpen} onClose={cancelHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.cancel")} detail={detail} />}>
        <Stack gap="md">
          <Textarea label={t("bulkAction.reasonOptional")} value={cancelReason} autosize minRows={2}
            onChange={(e) => setCancelReason(e.currentTarget.value)} />
          <PermissionLine code={required("cancel")} />
          <DialogFooter onCancel={cancelHandlers.close} submitLabel={t("actions.label.cancel")} onSubmit={handleCancel} busy={busy} />
        </Stack>
      </Modal>

      {/* CONFIRM REFUND (§20 — shared with the refund-queue per-row confirm) */}
      <RefundConfirmModal
        target={{
          id          : detail.id,
          returnNumber: detail.returnNumber,
          version     : detail.version,
          refundAmount: detail.refundAmount ?? null,
          refundMethod: detail.refundMethod ?? null,
          buyerName   : detail.buyerBusinessName,
          buyerRefCode: detail.buyerBusinessRefCode,
          buyerBankAccount: detail.buyerBankAccount
        }}
        opened={confirmOpen}
        onClose={confirmHandlers.close}
        onConfirmed={onChange}
      />

      {/* SET RESOLUTION */}
      <Modal opened={setResOpen} onClose={setResHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.setResolution")} detail={detail} />}>
        <Stack gap="md">
          <Select label={t("actionsDialog.resolution")} data={resolutionOptions(t)} value={resolution}
            onChange={(v) => v && setResolution(v as ResolutionType)} required allowDeselect={false} />
          {resolution === "REFUND_MONEY" ? (
            <Select label={t("actionsDialog.propose.method")} data={refundMethodOptions} value={refundMethod ?? null}
              onChange={(v) => setRefundMethod(v as RefundMethod)} required allowDeselect={false} />
          ) : null}
          <Textarea
            label={t("actionsDialog.reason")}
            required
            autosize
            minRows={3}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.currentTarget.value)}
            // O06 — BE Joi min(3): say so on the field, with the current length.
            error={(submitted || overrideLen > 0) && overrideLen < MIN_REASON
              ? t("actionsDialog.reasonMin", { min: MIN_REASON, count: overrideLen })
              : undefined}
          />
          <PermissionLine code={required("setResolution")} />
          <DialogFooter onCancel={setResHandlers.close} submitLabel={t("actionsDialog.setResolution.submit")} onSubmit={handleSetResolution} busy={busy}
            invalidReason={setResInvalid && (submitted || overrideLen > 0) ? t("actionsDialog.reasonMin", { min: MIN_REASON, count: overrideLen }) : null} />
        </Stack>
      </Modal>

      {/* ESCALATE TO MANAGER */}
      <Modal opened={escOpen} onClose={escHandlers.close} size={MODAL_W}
        title={<DialogTitle title={t("actions.label.escalate")} detail={detail} />}>
        <Stack gap="md">
          <Textarea
            label={t("actionsDialog.reason")}
            required
            autosize
            minRows={3}
            value={escReason}
            onChange={(e) => setEscReason(e.currentTarget.value)}
            error={(submitted || escReason.length > 0) && escInvalid
              ? t("actionsDialog.reasonMin", { min: MIN_REASON, count: escReason.trim().length })
              : undefined}
          />
          <PermissionLine code={required("escalate")} />
          <ConfirmNote helpKey={`${HELP}.confirm.escalate`} mt={0} />
          <DialogFooter onCancel={escHandlers.close} submitLabel={t("actionBar.forward")} onSubmit={handleEscalate} busy={busy}
            invalidReason={escInvalid && (submitted || escReason.length > 0) ? t("actionsDialog.reasonMin", { min: MIN_REASON, count: escReason.trim().length }) : null} />
        </Stack>
      </Modal>

      {/* MANAGER DISPUTE OVERRIDE (§18) */}
      <DisputeOverrideModal
        detail={detail}
        opened={overrideOpen}
        onClose={overrideHandlers.close}
        onResolved={onChange}
      />
    </>
  );
}
